/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1744761600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Apr 16 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "google-analytics",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Google Analytics",
          "depth": 0,
          "id": "connect-to-google-analytics"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing events",
          "depth": 1,
          "id": "syncing-events"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Validating events",
          "depth": 1,
          "id": "validating-events"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      h3: "h3",
      ul: "ul",
      li: "li",
      code: "code",
      h4: "h4",
      img: "img"
    }, _provideComponents(), props.components), {EventSyncWarning, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Events"
              })
            }), _jsx(_components.td, {
              children: "Sync events to GA4 via the Measurement Protocol API"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.google.com/analytics/devguides/collection/protocol/ga4/reference?client_type=firebase",
                children: "Measurement Protocol Reference"
              })
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-google-analytics",
        children: "Connect to Google Analytics"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Google Analytics"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Google Analytics"
        }), " by entering a Google Analytics ", _jsx(_components.strong, {
          children: "API Secret"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["To generate an API secret, navigate to ", _jsx(_components.strong, {
          children: "Admin > Data Streams > choose your stream > Measurement Protocol > Create"
        }), ". If you don't have any existing data streams you need to ", _jsx(_components.a, {
          href: "https://support.google.com/analytics/answer/9304153?sjid=1280583774983537157-NA#stream",
          children: "create one"
        }), " first."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Google Analytics destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Google Analytics destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-events",
        children: "Syncing events"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports sending following Google Analytics event types:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://developers.google.com/analytics/devguides/collection/protocol/ga4/reference?client_type=firebase",
            children: _jsx(_components.strong, {
              children: "Firebase App Events"
            })
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://developers.google.com/analytics/devguides/collection/protocol/ga4/reference?client_type=gtag#payload",
            children: _jsx(_components.strong, {
              children: "Gtag.js Events"
            })
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Firebase app events require an associated ", _jsx(_components.strong, {
          children: "Firebase app ID"
        }), " (", _jsx(_components.code, {
          children: "firebase_app_id"
        }), ") and ", _jsx(_components.strong, {
          children: "app instance ID"
        }), " (", _jsx(_components.code, {
          children: "app_instance_id"
        }), "). You can find the ", _jsx(_components.code, {
          children: "firebase_app_id"
        }), " in the Firebase console in ", _jsx(_components.strong, {
          children: "Project Settings > General > Your Apps > App ID"
        }), ". You can retrieve the ", _jsx(_components.code, {
          children: "app_instance_id"
        }), " through the ", _jsx(_components.a, {
          href: "https://firebase.google.com/docs/reference/android/com/google/firebase/analytics/FirebaseAnalytics#public-taskstring-getappinstanceid",
          children: "Firebase SDK"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you are having issues syncing your Firebase app events, check out ", _jsx(_components.a, {
          href: "https://developers.google.com/analytics/devguides/collection/protocol/ga4/troubleshooting?client_type=firebase",
          children: "Google's troubleshooting docs"
        }), "."]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["The ", _jsx(_components.code, {
            children: "app_instance_id"
          }), " isn't the same as your ", _jsx(_components.code, {
            children: "firebase_app_id"
          }), ". The ", _jsx(_components.code, {
            children: "firebase_app_id"
          }), " identifies your app uniquely, whereas ", _jsx(_components.code, {
            children: "app_instance_id"
          }), " identifies a single installation of the app uniquely."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Gtag.js events require an associated ", _jsx(_components.strong, {
          children: "measurement ID"
        }), " (", _jsx(_components.code, {
          children: "measurement_id"
        }), ") and ", _jsx(_components.strong, {
          children: "client ID"
        }), " (", _jsx(_components.code, {
          children: "client_id"
        }), "). You can find the ", _jsx(_components.code, {
          children: "measurement_id"
        }), " in the Google Analytics UI in ", _jsx(_components.strong, {
          children: "Admin > Data Streams > choose your stream > Measurement ID"
        }), ". You can retrieve the ", _jsx(_components.code, {
          children: "client_id"
        }), " using ", _jsx(_components.a, {
          href: "https://developers.google.com/tag-platform/gtagjs/reference#get",
          children: "these examples"
        }), " from Google."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you are having issues syncing your Gtag.js events, check out ", _jsx(_components.a, {
          href: "https://developers.google.com/analytics/devguides/collection/protocol/ga4/troubleshooting?client_type=gtag",
          children: "Google's troubleshooting docs"
        }), "."]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["All users of your website have the same ", _jsx(_components.code, {
            children: "measurement_id"
          }), ", but the ", _jsx(_components.code, {
            children: "client_id"
          }), " value is different for every user of your app."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to Google Analytics ", _jsx(_components.a, {
          href: "https://developers.google.com/analytics/devguides/collection/protocol/ga4/sending-events?client_type=firebase#required_parameters",
          children: "required parameters"
        }), " for more information."]
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h4, {
        id: "measurement-id",
        children: "Measurement ID"
      }), "\n", _jsxs(_components.p, {
        children: ["Your ", _jsx(_components.strong, {
          children: "measurement ID"
        }), " in Google Analytics is an identifier for a web data stream. It typically starts with ", _jsx(_components.code, {
          children: "UA"
        }), ", ", _jsx(_components.code, {
          children: "MO"
        }), ", or ", _jsx(_components.code, {
          children: "G"
        }), " followed by 10-20 characters, for example ", _jsx(_components.code, {
          children: "UA-12345678-9"
        }), ". You can find your measurement ID by following the ", _jsx(_components.strong, {
          children: "Find your Measurement ID"
        }), " link in ", _jsx(_components.a, {
          href: "https://support.google.com/analytics/answer/12270356?hl=en#:~:text=A%20Measurement%20ID%20is%20an,same%20as%20your%20destination%20ID",
          children: "Google's docs"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "You can also use the steps outlined in the preceding section."
      }), "\n", _jsx(_components.h4, {
        id: "event-name",
        children: "Event name"
      }), "\n", _jsxs(_components.p, {
        children: ["Google Analtyics requries an event name for all events. You can either provide a static value or select to ", _jsx(_components.strong, {
          children: "use a column"
        }), " from your model data. Hightouch syncs the static or column value as the ", _jsx(_components.code, {
          children: "events[].name"
        }), " parameter Google Analytics requires. See ", _jsx(_components.a, {
          href: "https://developers.google.com/analytics/devguides/collection/protocol/ga4/reference/events",
          children: "Google's events reference"
        }), " for all event name options."]
      }), "\n", _jsx(_components.h4, {
        id: "event-timestamp",
        children: "Event timestamp"
      }), "\n", _jsxs(_components.p, {
        children: ["Optionally, you can decide to select a model column that contains your event timestamps. Timestamps should be in ", _jsx(_components.a, {
          href: "https://www.iso.org/iso-8601-date-and-time-format.html",
          children: "ISO 8601"
        }), " format or ", _jsx(_components.a, {
          href: "https://en.wikipedia.org/wiki/Unix_time",
          children: "Unix timestamps"
        }), " in milliseconds, which are automatically converted to the format required by Google Analytics."]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "If this is selection is empty, Google Analytics creates an event timestamp by using the time the event arrives at their server."
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Field mapping is where you select which event parameters you want to send to Google Analytics. You can map data from any of your model columns to event parameters in Google Analytics. Ensure your model column data type matches the data type of the parameter you want to sync to."
      }), "\n", _jsxs(_components.p, {
        children: ["Different event types require different parameters. For example, ", _jsx(_components.code, {
          children: "purchase"
        }), " events require the following parameters:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "currency"
          }), " (string)"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "value"
          }), " (number)"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "transaction_id"
          }), " (string)"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "items"
          }), " (array with additional ", _jsx(_components.a, {
            href: "https://developers.google.com/analytics/devguides/collection/protocol/ga4/reference/events#purchase_item",
            children: "required parameters"
          }), ")"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-googleanalytics-field-mapping.png",
          alt: "Field mapping in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can use Hightouch's ", _jsx(_components.a, {
          href: "https://hightouch.com/docs/syncs/mapping-data#advanced-mapper",
          children: "advanced mapper"
        }), " to map ", _jsx(_components.a, {
          href: "/syncs/mapping-data#column-values",
          children: "column"
        }), ", ", _jsx(_components.a, {
          href: "/syncs/mapping-data#variable-values",
          children: "variable"
        }), ", and ", _jsx(_components.a, {
          href: "/syncs/mapping-data#static-values",
          children: "static"
        }), " values, for example, \"USD\" for ", _jsx(_components.code, {
          children: "currency"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "https://developers.google.com/analytics/devguides/collection/protocol/ga4/reference/events",
          children: "Google's events reference"
        }), " for the suggested parameters for each event."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "validating-events",
        children: "Validating events"
      }), "\n", _jsxs(_components.p, {
        children: ["According to the ", _jsx(_components.a, {
          href: "https://developers.google.com/analytics/devguides/collection/protocol/ga4/validating-events?client_type=gtag",
          children: "Google Analytics documentation"
        }), ", the Google Analytics Measurement Protocol for Google Analytics 4 does not return HTTP error codes, even if an event is malformed or missing required parameters. To ensure your events are valid, you should test them against the Measurement Protocol Validation Server before deploying them to production."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can run syncs using the Measurement Protocol Validation Server, if selected in your Hightouch sync config. Alternatively, before running your sync in Hightouch, use this ", _jsx(_components.a, {
          href: "https://ga-dev-tools.google/ga4/event-builder/",
          children: "tool from Google Analytics"
        }), " to validate events and make sure you're not missing any parameters."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you don't validate the event and are missing parameters in the ", _jsx(_components.a, {
          href: "#field-mapping",
          children: "field mapping"
        }), " section, the sync will look healthy in Hightouch because the response from GA4 won't contain errors, but you won't see events in GA4."]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
